import React, { useState, useRef, useEffect, useCallback } from 'react';
import { CaptureOverlay } from '../shared';
import { safeExitPiP } from '../../../../utils/mediaUtils';

interface GuidedFacecamProps {
  taskName: string;
  isAndroid: boolean;
  /** Parent-owned stream ref — persists across step changes so PiP stays alive */
  faceStreamRef: React.MutableRefObject<MediaStream | null>;
  onNext: () => void;
  onClose: () => void;
}

export const GuidedFacecam: React.FC<GuidedFacecamProps> = ({
  taskName,
  isAndroid,
  faceStreamRef,
  onNext,
  onClose,
}) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [isCamReady, setIsCamReady] = useState(false);
  const [isPipActive, setIsPipActive] = useState(false);
  const [pipError, setPipError] = useState<string | null>(null);

  const startCamera = useCallback(async () => {
    // Reuse existing stream if parent already has one (e.g. navigating back)
    if (faceStreamRef.current) {
      if (videoRef.current) {
        videoRef.current.srcObject = faceStreamRef.current;
        videoRef.current.muted = true;
        await videoRef.current.play().catch(() => {});
      }
      setIsCamReady(true);
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user' },
        audio: false,
      });
      faceStreamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.muted = true;
        await videoRef.current.play();
      }
      setIsCamReady(true);
      setPipError(null);
    } catch {
      setPipError('Could not access camera. Please check permissions.');
    }
  }, [faceStreamRef]);

  const requestPip = useCallback(async () => {
    if (!videoRef.current) return;
    try {
      await videoRef.current.requestPictureInPicture();
      setIsPipActive(true);
      setPipError(null);
    } catch {
      setPipError('Floating camera (PiP) is not supported or blocked on this device.');
    }
  }, []);

  useEffect(() => {
    startCamera();
    // Do NOT stop tracks on unmount — parent owns the stream.
    // PiP needs the stream alive across countdown/launched/upload steps.
    // Parent's handleClose + final unmount cleanup handles stopping tracks.
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Track PiP exit + keep-alive on focus (matching legacy lines 686-712)
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const onLeavePip = () => setIsPipActive(false);
    video.addEventListener('leavepictureinpicture', onLeavePip);

    // Sync stream to video element
    if (faceStreamRef.current && video.srcObject !== faceStreamRef.current) {
      video.srcObject = faceStreamRef.current;
      video.play().catch(() => {});
    }

    // Keep-alive: resume playback on focus
    const handleFocus = () => {
      if (faceStreamRef.current && video.paused) {
        video.play().catch(() => {});
      }
    };
    window.addEventListener('focus', handleFocus);

    return () => {
      video.removeEventListener('leavepictureinpicture', onLeavePip);
      window.removeEventListener('focus', handleFocus);
    };
  }, [isCamReady, faceStreamRef]);

  const handleClose = () => {
    safeExitPiP();
    onClose();
  };

  // Android PiP enforcement: require PiP before allowing countdown
  const handleNext = () => {
    if (isAndroid && !isPipActive) {
      requestPip();
      return;
    }
    onNext();
  };

  return (
    <CaptureOverlay taskName={taskName} onClose={handleClose}>
      <div className="gc-scroll-body">
        <div className="gc-section-heading">Step 1 of 2 · Face Camera</div>

        {/* Live cam preview */}
        <div className="gc-cam-preview">
          <video ref={videoRef} autoPlay playsInline muted />
          {isCamReady && <div className="gc-live-badge">LIVE</div>}
        </div>

        {/* PiP info card */}
        {isCamReady && !isPipActive && (
          <div className="gc-info-card">
            <div className="gc-info-title">Floating Camera (PiP)</div>
            <div className="gc-info-desc">
              Your face cam will float over the target app while you record. Tap the button to activate Picture-in-Picture.
            </div>
            <button className="gc-btn-pip" onClick={requestPip}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#D4715A" strokeWidth="2" strokeLinecap="round">
                <rect x="2" y="2" width="20" height="20" rx="2" />
                <rect x="12" y="12" width="8" height="8" rx="1" />
              </svg>
              Pop-out Camera {isAndroid ? '(Required)' : '(Optional)'}
            </button>
          </div>
        )}

        {/* PiP success feedback */}
        {isPipActive && (
          <div className="gc-info-card gc-pip-success">
            <div className="gc-info-title">Camera is Floating!</div>
            <div className="gc-info-desc">
              You can move the bubble around your screen. It will stay visible while you record.
            </div>
          </div>
        )}

        {/* PiP error */}
        {pipError && (
          <div className="gc-info-card gc-pip-error">
            <div className="gc-info-desc">{pipError}</div>
          </div>
        )}
      </div>

      <div className="gc-bottom-simple">
        <button
          className="gc-btn-cta"
          disabled={!isCamReady}
          style={isCamReady ? undefined : { opacity: 0.4, pointerEvents: 'none' }}
          onClick={handleNext}
        >
          {isAndroid && !isPipActive ? 'Pop-out Camera (Required)' : 'Launch & Record'}
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round">
            <path d="M5 12h14M12 5l7 7-7 7" />
          </svg>
        </button>
      </div>
    </CaptureOverlay>
  );
};
